<template>
  <DropdownMenu>
    <DropdownMenuTrigger as-child>
      <BaseButton
        variant="ghost"
        class="flex items-center space-x-1 py-1 pl-1 hover:bg-background rounded-[40px] text-sm font-semibold text-purple-gray border border-[#262638] hover:text-lavender"
      >
        <UserAvatar
          :wallet-address="walletConnected"
          class="size-8"
        />
        <div
          v-if="!accountData?.embeddedWalletInfo"
          class="flex justify-center items-center"
        >
          <BaseSpinner />
        </div>
        <span v-else>{{ accountData?.embeddedWalletInfo?.user?.email || shortWallet }}</span>
        <ChevronDown :size="16" />
      </BaseButton>
    </DropdownMenuTrigger>

    <DropdownMenuContent class="w-72 bg-dark2 border-none rounded-2xl">
      <div class="p-4 space-y-4 text-purple-gray text-sm tracking-[0.03rem] font-semibold">
        <div v-if="userEmail">
          <div class="text-base text-light1">
            Email
          </div>
          <div class="text-purple-gray">
            {{ userEmail }}
          </div>
        </div>

        <div>
          <div class="text-base text-light1">
            Wallet address
          </div>

          <BaseClipboard
            :text="shortWallet"
            :copy-value="accountData?.address || ''"
          />
        </div>

        <div class="grid grid-cols-2 gap-x-1 gap-y-2">
          <template v-if="isWalletConnected">
            <BaseButton
              as="a"
              href="https://buy.kryptonim.com/lingo"
              target="_blank"
              variant="outline"
              class="flex-1 bg-dark2 border-dark3 h-10 text-light1 rounded-full"
            >
              Buy
            </BaseButton>

            <BaseButton
              as="a"
              href="https://bridge.lingocoin.io/"
              target="_blank"
              variant="outline"
              class="flex-1 bg-dark2 border-dark3 h-10 text-light1 rounded-full"
            >
              Bridge
            </BaseButton>

            <BaseButton
              :disabled="!isWalletConnected"
              variant="outline"
              class="flex-1 bg-dark2 border-dark3 h-10 text-light1 rounded-full"
              @click="emit('open-wallet')"
            >
              Open Wallet
            </BaseButton>

            <BaseButton
              variant="outline"
              class="flex-1 bg-dark2 border-dark3 h-10 text-light1 rounded-full"
              @click="logout"
            >
              <LogOut
                class="text-purple-gray"
                :size="20"
              />
              Log Out
            </BaseButton>
          </template>

          <BaseButton
            v-else
            variant="outline"
            class="col-start-1 ml-auto flex-1 bg-dark2 border-dark3 h-10 text-light1 rounded-full"
            @click="login"
          >
            <LogIn
              class="text-purple-gray"
              :size="20"
            />
            Log In
          </BaseButton>
        </div>
      </div>
    </DropdownMenuContent>
  </DropdownMenu>
</template>

<script setup lang="ts">
import { ChevronDown, LogIn, LogOut } from 'lucide-vue-next'
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import UserAvatar from '@/components/UserAvatar.vue'
import { BaseButton } from '@/components/ui/button'
import BaseClipboard from '@/components/ui/clipboard/BaseClipboard.vue'
import { isWalletConnected, walletConnected } from '@/composables/auth.ts'
import { useWalletAuth } from '@/composables/wallet/use-wallet-auth.ts'
import type { UseAppKitAccountReturn } from '@reown/appkit'
import { useGetMe } from '@/composables/get-me.ts'
import { computed } from 'vue'
import BaseSpinner from '@/components/ui/BaseSpinner.vue'

defineProps<{
	shortWallet?: string | null
	accountData?: UseAppKitAccountReturn
}>()

const emit = defineEmits<{
	(e: 'open-wallet'): void
}>()
const { login, logout } = useWalletAuth()

const { meData } = useGetMe()
const userEmail = computed(() => meData.value?.email)
</script>
